import { Data } from '.generated/Foundation.CW.model';
import { LinkField, useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import ImageWrapper from 'helpers/SitecoreWrappers/ImageWrapper/ImageWrapper';
import LinkWrapper from 'helpers/SitecoreWrappers/LinkWrapper/LinkWrapper';
import PlainTextWrapper from 'helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import RichTextWrapper from 'helpers/SitecoreWrappers/RichTextWrapper/RichTextWrapper';
import { SvgIcon } from 'helpers/SvgIcon';
import { TransformedSiteSettings } from 'lib/page-props-factory/plugins/site-settings';
import Link from 'next/link';
import useDictionary from 'lib/hooks/useDictionary';
import useIsEditing from 'lib/hooks/useIsEditing';

export type AuthorCardProps = Data.PageSpecific.Author;
export type CategoryProps = Data.PageSpecific.BlogCategory;

const AuthorCard = () => {
  const { sitecoreContext } = useSitecoreContext();
  const contextWithSettings = sitecoreContext?.route;
  const authorCardFields = contextWithSettings?.fields?.author as AuthorCardProps;
  const categoryFields = contextWithSettings?.fields?.category as CategoryProps;
  const siteSettingsField = sitecoreContext?.siteSettings as TransformedSiteSettings;
  const blogSearchPage = siteSettingsField?.blogSearchPage as LinkField;
  const { getDictionaryValue } = useDictionary();
  const isEditing = useIsEditing();

  return (
    <section className="lg:px-[80px] px-10 mb-[48px] max-w-screen-xxl mx-auto">
      <div className="lg:p-[30px] p-12 border-2 border-gray-50 rounded-[8px]">
        <div className="lg:flex gap-[46px]">
          {(authorCardFields?.fields?.authorImage?.value?.src || isEditing) && (
            <div>
              <div className="w-[132px] h-[166px] mb-[32px] lg:mb-0">
                <ImageWrapper
                  field={authorCardFields?.fields?.authorImage}
                  className="aspect-[2/3] object-cover w-full rounded-sm h-full"
                />
              </div>
            </div>
          )}

          <div className="flex-1">
            <p className="text-16 leading-31 font-bold font-centuryGothic text-gray-900 lg:mb-4 mb-5">
              Written by
            </p>
            <div className="text-28 text-gray-900 leading-31 font-bold font-centuryGothic flex gap-[8px]">
              <p aria-label={authorCardFields?.fields?.authorName?.value}>
                <PlainTextWrapper field={authorCardFields?.fields?.authorName} />
              </p>
              {/* <p aria-label={authorCardFields?.fields?.authorCredentials?.value}>
                {authorCardFields?.fields?.authorCredentials?.value !== '' && ', '}
                <PlainTextWrapper field={authorCardFields?.fields?.authorCredentials} />
              </p> */}
              <p aria-label={authorCardFields?.fields?.authorCredentials?.value}>
                {authorCardFields?.fields?.authorCredentials?.value?.trim() ? (
                  <>
                    , <PlainTextWrapper field={authorCardFields.fields.authorCredentials} />
                  </>
                ) : null}
              </p>
            </div>
            <PlainTextWrapper
              field={authorCardFields?.fields?.authorTitle}
              tag="p"
              className="text-18 leading-28 font-nunitoSans text-gray-900 pt-5"
            />
            <LinkWrapper
              field={authorCardFields?.fields?.locationPageLink}
              className="!text-blue-base !text-18 !leading-28 font-nunitoSans pt-5 block hover:!text-darkblue-base hover:cursor-pointer hover:!underline"
              ctaVariant="hyperlink"
            >
              <PlainTextWrapper field={authorCardFields?.fields?.location} />
            </LinkWrapper>
            <RichTextWrapper
              field={categoryFields?.fields?.description}
              className="pt-[32px] lg:block hidden"
            />
          </div>
          <div className="flex flex-col ml-auto lg:mt-0 mt-16">
            {authorCardFields?.fields?.authorPageLink?.value?.href !== '' && (
              <LinkWrapper
                field={authorCardFields?.fields?.authorPageLink}
                ctaVariant="tertiary"
                ctaIcon={'arrow-right'}
                className="!text-blue-base w-fit"
              >
                {getDictionaryValue('ViewProviderProfileLinkLabel')}
              </LinkWrapper>
            )}

            {(authorCardFields?.fields?.authorName?.value || isEditing) && (
              <Link
                href={`${blogSearchPage?.value?.href}?authorName=${authorCardFields?.fields?.authorName?.value}`}
                className="!text-blue-base w-fit flex-row theme-text-btn border-theme-text-btn-border lg:flex inline font-bold font-centuryGothic gap-x-4 items-center justify-center py-7 !text-18 !leading-25 rounded-sm lg:[&>svg]:inline-block [&>svg]:inline lg:[&>svg]:ml-0 [&>svg]:ml-[10px] lg:text-center text-left text-theme-text-btn-text active:bg-theme-text-btn-hover active:text-theme-text-btn-hover-text active:border-theme-text-btn-hover-border  focus:outline-offset-2 focus:outline-2 focus:outline-theme-text-btn-outline focus:border-theme-text-btn-border focus:outline hover:text-theme-text-btn-hover-text hover:underline hover:border-theme-text-btn-hover-border"
              >
                {getDictionaryValue('MoreFromThisAuthorLinkLabel')}
                <SvgIcon icon="arrow-right" size="lg" />
              </Link>
            )}
            <RichTextWrapper
              field={categoryFields?.fields?.description}
              className="pt-[32px] block lg:hidden"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default AuthorCard;
